import { sharedRoutes } from "@shared/data/sharedRoutes";
import { isProduction } from "@shared/utils/fullscriptEnv/fullscriptEnv";

type LoginRegion = "us" | "ca" | "nova";

const NOVA_LOGIN_PATH = "/n/login";

const PRODUCTION_REGION_ORIGIN: Record<"us" | "ca", string> = {
  us: "https://us.fullscript.com",
  ca: "https://ca.fullscript.com",
};

const loginPathFor = (region: LoginRegion) =>
  region === "nova" ? NOVA_LOGIN_PATH : sharedRoutes.login;

/**
 * Builds the sign-in URL for a region. Locally each region lives on its own subdomain;
 * production regions live on their own domain; other deployed environments share one host
 * and select the region with the `fs_region` param read by `RegionConfig::ActiveRecord::RegionSelector`.
 *
 * `slug` only applies to the production case — it is the sign-in path to carry across the
 * domain hop (e.g. `/sso_login`). Elsewhere the region's own login path is already correct.
 */
const regionLoginHref = (region: LoginRegion, slug = "") => {
  const { hostname, port, protocol } = window.location;
  const isLocal = hostname === "localhost" || hostname.endsWith(".localhost");

  if (isLocal) {
    const portSuffix = port ? `:${port}` : "";

    return `${protocol}//${region}.localhost${portSuffix}${loginPathFor(region)}`;
  }

  if (region === "nova") return NOVA_LOGIN_PATH;

  if (isProduction()) return `${PRODUCTION_REGION_ORIGIN[region]}${slug}`;

  return `${sharedRoutes.login}?fs_region=${region}`;
};

export { regionLoginHref, type LoginRegion };
